import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

const PRICE = 39;
const STEPS = ["Couverture actuelle", "Nouveau format & imprimeur", "Modifications", "Vos fichiers", "Récapitulatif"];

const Radio = ({ name, checked, onChange, label, testid }) => (
  <label className={`flex items-start gap-2 px-3 py-2 border cursor-pointer text-[14px] ${checked ? "border-[#0E7C90] bg-[#0E7C90]/5" : "border-black/20 bg-white"}`}>
    <input type="radio" name={name} checked={checked} onChange={onChange} data-testid={testid} className="mt-1" /><span>{label}</span>
  </label>
);
const Chk = ({ checked, onChange, label, testid }) => (
  <label className={`flex items-start gap-2 px-3 py-2 border cursor-pointer text-[13px] ${checked ? "border-[#0E7C90] bg-[#0E7C90]/5" : "border-black/20 bg-white"}`}>
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="mt-1" /><span>{label}</span>
  </label>
);
const SB = ({ title, children }) => <div><div className="cs-eyebrow text-neutral-500 mb-2">{title}</div><div className="text-[14px] space-y-1">{children}</div></div>;
const SR = ({ k, v }) => v ? <div className="grid grid-cols-[180px_1fr] gap-3"><span className="text-neutral-500">{k}</span><span className="text-neutral-800 whitespace-pre-wrap">{v}</span></div> : null;

const REASONS = [
  { k: "printer", l: "Changement d'imprimeur / plateforme" },
  { k: "format", l: "Changement de format" },
  { k: "spine", l: "Modification de l'épaisseur du dos" },
  { k: "specs", l: "Nouvelles spécifications techniques de l'imprimeur" },
  { k: "multiple", l: "Plusieurs de ces raisons" },
  { k: "other", l: "Autre" },
];

export default function AdapterCouvertureConfigurator() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  // Step 1
  const [coverType, setCoverType] = useState("");
  const [reasons, setReasons] = useState([]);
  const [reasonOther, setReasonOther] = useState("");
  const [details, setDetails] = useState("");

  // Step 2
  const [printer, setPrinter] = useState("");
  const [tplProvided, setTplProvided] = useState("unknown");
  const [tplFiles, setTplFiles] = useState([]);
  const [widthMm, setWidthMm] = useState("");
  const [heightMm, setHeightMm] = useState("");
  const [pageCount, setPageCount] = useState("");
  const [spineKnown, setSpineKnown] = useState("no");
  const [spineMm, setSpineMm] = useState("");
  const [binding, setBinding] = useState("unknown");
  const [bindingOther, setBindingOther] = useState("");
  const [specsNotes, setSpecsNotes] = useState("");
  const [specsFiles, setSpecsFiles] = useState([]);

  // Step 3
  const [modMode, setModMode] = useState("adaptation_only");
  const [modNotes, setModNotes] = useState("");

  // Step 4
  const [currentFiles, setCurrentFiles] = useState([]);
  const [noSource, setNoSource] = useState(false);
  const [newLogoFiles, setNewLogoFiles] = useState([]);
  const [newBarcodeFiles, setNewBarcodeFiles] = useState([]);
  const [joboptionsFiles, setJoboptionsFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);

  // Step 5
  const [confirmChk, setConfirmChk] = useState(false);

  const isFull = coverType === "full";

  const canNext = useMemo(() => {
    if (step === 0) {
      if (!coverType) return false;
      if (reasons.length === 0) return false;
      if (reasons.includes("other") && !reasonOther.trim()) return false;
      return true;
    }
    if (step === 2) {
      if (modMode === "small_changes" && !modNotes.trim()) return false;
      return true;
    }
    if (step === 3) return currentFiles.length > 0;
    return true;
  }, [step, coverType, reasons, reasonOther, modMode, modNotes, currentFiles]);

  const submit = async () => {
    if (!confirmChk) return toast.error("Cochez la confirmation");
    if (currentFiles.length === 0) return toast.error("Merci de transmettre votre couverture actuelle");
    setSubmitting(true);
    try {
      const payload = {
        current: {
          cover_type: coverType,
          reasons,
          reason_other: reasons.includes("other") ? reasonOther.trim() : "",
          details: details.trim(),
        },
        new_format: {
          printer_platform: printer.trim(),
          template_provided: tplProvided,
          template_file_ids: tplFiles.map(f => f.id).filter(Boolean),
          width_mm: Number(widthMm) || null,
          height_mm: Number(heightMm) || null,
          page_count: isFull ? (Number(pageCount) || null) : null,
          spine_known: isFull ? spineKnown : "no",
          spine_mm: isFull && spineKnown === "yes" ? (Number(spineMm) || null) : null,
          binding: isFull ? binding : "unknown",
          binding_other: isFull && binding === "other" ? bindingOther.trim() : "",
          printer_specs_notes: specsNotes.trim(),
          printer_specs_file_ids: specsFiles.map(f => f.id).filter(Boolean),
        },
        modifications: {
          mode: modMode,
          notes: modMode === "small_changes" ? modNotes.trim() : "",
        },
        files: {
          current_cover_file_ids: currentFiles.map(f => f.id).filter(Boolean),
          no_source_file: noSource,
          new_template_file_ids: tplFiles.map(f => f.id).filter(Boolean),
          new_logo_file_ids: newLogoFiles.map(f => f.id).filter(Boolean),
          new_barcode_file_ids: newBarcodeFiles.map(f => f.id).filter(Boolean),
          joboptions_file_ids: [...specsFiles, ...joboptionsFiles].map(f => f.id).filter(Boolean),
          other_file_ids: otherFiles.map(f => f.id).filter(Boolean),
        },
        confirm: true,
      };
      const { data } = await api.post("/adapter-couverture/order", payload);
      toast.success("Commande enregistrée");
      nav(`/espace/commande/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const bindLabel = binding === "softcover" ? "Broché / couverture souple"
    : binding === "hardcover" ? "Relié / couverture rigide"
    : binding === "other" ? `Autre — ${bindingOther || "à préciser"}`
    : "Je ne sais pas";

  return (
    <div className="cs-cream min-h-[85vh] py-10" data-testid="page-adapter-couverture-configurator">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
        <Link to="/services/couvertures" className="text-sm font-mono uppercase tracking-[0.16em] text-neutral-500 hover:text-black inline-flex items-center gap-2"><ArrowLeft size={14} /> Retour</Link>
        <div className="mt-4">
          <div className="cs-eyebrow text-[#0E7C90]">Couvertures · Livre</div>
          <h1 className="cs-h1 mt-2 text-3xl lg:text-5xl">Adaptation d'une couverture existante — 39 €</h1>
          <p className="mt-3 text-neutral-700 text-[15px] leading-relaxed max-w-2xl">
            Adapter une création existante à un nouveau format, un nouveau gabarit ou un nouvel imprimeur, sans refaire sa conception graphique. Livrable : PDF final prêt imprimeur.
          </p>
        </div>
        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {STEPS.map((s, i) => (
            <div key={i} className={`flex items-center gap-2 text-[12px] font-mono uppercase tracking-widest ${i === step ? "text-[#0E7C90]" : i < step ? "text-neutral-800" : "text-neutral-400"}`}>
              <span className={`inline-flex items-center justify-center w-6 h-6 border ${i === step ? "border-[#0E7C90] bg-[#0E7C90]/10" : i < step ? "border-black/50 bg-black text-white" : "border-black/20"}`}>{i < step ? <Check size={12} /> : i + 1}</span>
              <span className="hidden md:inline">{s}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          {step === 0 && (
            <section className="cs-card p-6 space-y-4" data-testid="ac-step-current">
              <h2 className="cs-h3 text-2xl">Votre couverture actuelle</h2>
              <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Quel type de couverture souhaitez-vous adapter ? *</span>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  <Radio name="act" checked={coverType === "front_only"} onChange={() => setCoverType("front_only")} label="Première de couverture seule" testid="ac-type-front" />
                  <Radio name="act" checked={coverType === "full"} onChange={() => setCoverType("full")} label="Couverture complète : 1re + dos + 4e" testid="ac-type-full" />
                </div>
              </div>
              <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Pourquoi souhaitez-vous l'adapter ? * (plusieurs choix)</span>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {REASONS.map(r => (
                    <Chk key={r.k} checked={reasons.includes(r.k)} onChange={() => toggle(reasons, setReasons, r.k)} label={r.l} testid={`ac-reason-${r.k}`} />
                  ))}
                </div>
                {reasons.includes("other") && (
                  <input value={reasonOther} onChange={e => setReasonOther(e.target.value)} placeholder="Précisez…" className="cs-input mt-2 w-full" data-testid="ac-reason-other" />
                )}
              </div>
              <label className="block">
                <span className="text-[11px] font-mono text-neutral-500 uppercase">Précisions sur votre demande</span>
                <textarea rows={4} value={details} onChange={e => setDetails(e.target.value)} className="cs-input mt-1 w-full resize-none" data-testid="ac-details"
                  placeholder="Expliquez simplement ce qui doit changer par rapport au fichier actuel." />
              </label>
            </section>
          )}

          {step === 1 && (
            <section className="cs-card p-6 space-y-4" data-testid="ac-step-new-format">
              <h2 className="cs-h3 text-2xl">Nouveau format & imprimeur</h2>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase">Nom du nouvel imprimeur / plateforme</span>
                <input value={printer} onChange={e => setPrinter(e.target.value)} className="cs-input mt-1 w-full" data-testid="ac-printer"
                  placeholder="Ex. : Amazon KDP, Bookelis, BoD, imprimeur local…" />
              </label>
              <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Votre imprimeur vous a-t-il fourni un nouveau gabarit ?</span>
                <div className="mt-2 grid sm:grid-cols-3 gap-2">
                  <Radio name="atp" checked={tplProvided === "yes"} onChange={() => setTplProvided("yes")} label="Oui" testid="ac-tpl-yes" />
                  <Radio name="atp" checked={tplProvided === "no"} onChange={() => setTplProvided("no")} label="Non" testid="ac-tpl-no" />
                  <Radio name="atp" checked={tplProvided === "unknown"} onChange={() => setTplProvided("unknown")} label="Je ne sais pas" testid="ac-tpl-unknown" />
                </div>
                {tplProvided === "yes" && <div className="mt-3"><FileUploader files={tplFiles} setFiles={setTplFiles} testId="ac-tpl-upload" /></div>}
              </div>
              <div className="border-t border-black/10 pt-4">
                <p className="text-[13px] text-neutral-600 mb-3">Si aucun gabarit complet n'est fourni, précisez :</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase">Largeur (mm)</span><input type="number" value={widthMm} onChange={e => setWidthMm(e.target.value)} className="cs-input mt-1 w-full" data-testid="ac-width" /></label>
                  <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase">Hauteur (mm)</span><input type="number" value={heightMm} onChange={e => setHeightMm(e.target.value)} className="cs-input mt-1 w-full" data-testid="ac-height" /></label>
                  {isFull && (
                    <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase">Nb pages définitif</span><input type="number" value={pageCount} onChange={e => setPageCount(e.target.value)} className="cs-input mt-1 w-full" data-testid="ac-pages" /></label>
                  )}
                </div>
                {isFull && (<>
                  <div className="mt-3"><span className="text-[11px] font-mono text-neutral-500 uppercase">Connaissez-vous l'épaisseur exacte du nouveau dos ?</span>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      <Radio name="ask" checked={spineKnown === "yes"} onChange={() => setSpineKnown("yes")} label="Oui" testid="ac-spine-yes" />
                      <Radio name="ask" checked={spineKnown === "no"} onChange={() => setSpineKnown("no")} label="Non — à vérifier avec l'imprimeur" testid="ac-spine-no" />
                    </div>
                    {spineKnown === "yes" && <input type="number" step="0.1" value={spineMm} onChange={e => setSpineMm(e.target.value)} placeholder="Épaisseur en mm" className="cs-input mt-2 w-40" data-testid="ac-spine-mm" />}
                  </div>
                  <div className="mt-3"><span className="text-[11px] font-mono text-neutral-500 uppercase">Type de reliure</span>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      <Radio name="abn" checked={binding === "softcover"} onChange={() => setBinding("softcover")} label="Broché / couverture souple" testid="ac-bind-soft" />
                      <Radio name="abn" checked={binding === "hardcover"} onChange={() => setBinding("hardcover")} label="Relié / couverture rigide" testid="ac-bind-hard" />
                      <Radio name="abn" checked={binding === "other"} onChange={() => setBinding("other")} label="Autre" testid="ac-bind-other" />
                      <Radio name="abn" checked={binding === "unknown"} onChange={() => setBinding("unknown")} label="Je ne sais pas" testid="ac-bind-unknown" />
                    </div>
                    {binding === "other" && <input value={bindingOther} onChange={e => setBindingOther(e.target.value)} placeholder="Précisez…" className="cs-input mt-2 w-full" data-testid="ac-bind-other-txt" />}
                  </div>
                </>)}
                <p className="text-[12px] text-neutral-500 mt-3">Lorsqu'un gabarit ou une valeur officielle est fourni par l'imprimeur, cette donnée prévaut.</p>
              </div>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase">Consignes techniques / Joboptions</span><textarea rows={3} value={specsNotes} onChange={e => setSpecsNotes(e.target.value)} className="cs-input mt-1 w-full resize-none" data-testid="ac-specs-notes" /></label>
              <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Document technique / Joboptions</span><FileUploader files={specsFiles} setFiles={setSpecsFiles} testId="ac-specs-files" /></div>
            </section>
          )}

          {step === 2 && (
            <section className="cs-card p-6 space-y-4" data-testid="ac-step-modifications">
              <h2 className="cs-h3 text-2xl">Modifications demandées</h2>
              <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Souhaitez-vous uniquement l'adaptation technique ?</span>
                <div className="mt-2 grid gap-2">
                  <Radio name="amod" checked={modMode === "adaptation_only"} onChange={() => setModMode("adaptation_only")} label="Oui, conserver la création graphique actuelle" testid="ac-mod-adapt" />
                  <Radio name="amod" checked={modMode === "small_changes"} onChange={() => setModMode("small_changes")} label="J'ai également quelques petites modifications à signaler" testid="ac-mod-small" />
                </div>
              </div>
              {modMode === "small_changes" && (
                <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase">Détaillez vos petites modifications *</span>
                  <textarea rows={5} value={modNotes} onChange={e => setModNotes(e.target.value)} className="cs-input mt-1 w-full resize-none" data-testid="ac-mod-notes"
                    placeholder="Ex. : Remplacement d'un prix, ISBN, logo, correction d'un texte court…" />
                </label>
              )}
              <div className="p-4 bg-neutral-50 border border-black/10 text-[13px] text-neutral-700 leading-relaxed">
                Cette prestation conserve la création graphique existante. Une modification importante de la composition, des images, du style ou du concept pourra nécessiter une prestation complémentaire ou un devis.
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="cs-card p-6 space-y-5" data-testid="ac-step-files">
              <h2 className="cs-h3 text-2xl">Vos fichiers</h2>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase">Fichier de la couverture actuelle *</span>
                <p className="text-[13px] text-neutral-600 mt-1">Transmettez le fichier de meilleure qualité dont vous disposez. Un fichier source éditable facilite l'adaptation.</p>
                <div className="mt-2"><FileUploader files={currentFiles} setFiles={setCurrentFiles} testId="ac-current-upload" /></div>
                <div className="mt-2"><Chk checked={noSource} onChange={() => setNoSource(v => !v)} label="Je ne possède pas le fichier source éditable" testid="ac-no-source" /></div>
                <p className="text-[12px] text-neutral-500 mt-2">Un PDF correctement construit peut parfois suffire. Si le fichier fourni nécessite une reconstruction importante avant adaptation, Charbonier Studio vous proposera une solution et un tarif avant toute intervention supplémentaire.</p>
              </div>
              <div className="border-t border-black/10 pt-4 space-y-4">
                <p className="text-[13px] text-neutral-600">Vous pouvez également transmettre :</p>
                <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Nouveau logo (éditeur ou autre)</span><FileUploader files={newLogoFiles} setFiles={setNewLogoFiles} testId="ac-newlogo-upload" /></div>
                <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Nouveau code-barres</span><FileUploader files={newBarcodeFiles} setFiles={setNewBarcodeFiles} testId="ac-newbar-upload" /></div>
                <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Joboptions imprimeur supplémentaires</span><FileUploader files={joboptionsFiles} setFiles={setJoboptionsFiles} testId="ac-joboptions-upload" /></div>
                <div><span className="text-[11px] font-mono text-neutral-500 uppercase">Autre élément utile</span><FileUploader files={otherFiles} setFiles={setOtherFiles} testId="ac-other-upload" /></div>
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="cs-card p-6 space-y-5" data-testid="ac-step-summary">
              <h2 className="cs-h3 text-2xl">Récapitulatif</h2>
              <SB title="Prestation">Adaptation d'une couverture existante — 39 €</SB>
              <SB title="Couverture actuelle">
                <SR k="Type" v={coverType === "full" ? "Couverture complète (1re + dos + 4e)" : coverType === "front_only" ? "Première de couverture seule" : "—"} />
                <SR k="Raison(s)" v={reasons.map(r => (REASONS.find(x => x.k === r) || {}).l).filter(Boolean).join(", ") + (reasons.includes("other") && reasonOther ? ` — ${reasonOther}` : "")} />
                <SR k="Précisions" v={details} />
              </SB>
              <SB title="Nouveau format / imprimeur">
                <SR k="Imprimeur" v={printer} />
                <SR k="Gabarit imprimeur" v={tplProvided === "yes" ? `Fourni (${tplFiles.length} fichier(s))` : tplProvided === "no" ? "Non fourni" : "À vérifier"} />
                <SR k="Format fini" v={widthMm && heightMm ? `${widthMm} × ${heightMm} mm` : "—"} />
                {isFull && <>
                  <SR k="Nombre de pages" v={pageCount} />
                  <SR k="Épaisseur dos" v={spineKnown === "yes" && spineMm ? `${spineMm} mm` : "À vérifier avec l'imprimeur"} />
                  <SR k="Reliure" v={bindLabel} />
                </>}
                <SR k="Consignes techniques" v={specsNotes} />
              </SB>
              <SB title="Modifications">
                <SR k="Nature" v={modMode === "small_changes" ? "Adaptation + petites modifications" : "Adaptation technique seule"} />
                {modMode === "small_changes" && <SR k="Détails" v={modNotes} />}
              </SB>
              <SB title="Fichiers transmis">
                <ul className="text-[13px] space-y-1 mt-1">
                  {currentFiles.map(f => <li key={f.id}>• Couverture actuelle : {f.original_name}</li>)}
                  {noSource && <li>• Note : pas de fichier source éditable</li>}
                  {tplFiles.map(f => <li key={f.id}>• Nouveau gabarit : {f.original_name}</li>)}
                  {newLogoFiles.map(f => <li key={f.id}>• Nouveau logo : {f.original_name}</li>)}
                  {newBarcodeFiles.map(f => <li key={f.id}>• Nouveau code-barres : {f.original_name}</li>)}
                  {[...specsFiles, ...joboptionsFiles].map(f => <li key={f.id}>• Joboptions / consignes : {f.original_name}</li>)}
                  {otherFiles.map(f => <li key={f.id}>• Autre : {f.original_name}</li>)}
                </ul>
              </SB>
              <SB title="Livraison">PDF final prêt imprimeur</SB>
              <SB title="Délai">3 à 5 jours ouvrés</SB>
              <div className="p-4 bg-neutral-50 border border-black/10 text-[12px] text-neutral-600 leading-relaxed">
                <strong>Inclus dans les 39 €</strong> : adaptation au nouveau format / gabarit, recalage des éléments si nécessaire, ajustement des fonds perdus, ajustement du dos / tranche pour une couverture complète, prise en compte des spécifications du nouvel imprimeur, export PDF final prêt imprimeur, <strong>1 groupe de corrections légères inclus</strong> après livraison. Groupe supplémentaire : +19 €, uniquement après accord préalable.<br /><br />
                La création graphique existante est conservée. Cette prestation ne comprend pas une nouvelle conception de couverture.
              </div>
              <label className="flex items-start gap-2 text-[13px]">
                <input type="checkbox" checked={confirmChk} onChange={e => setConfirmChk(e.target.checked)} data-testid="ac-confirm" className="mt-1" />
                <span>Je confirme que les informations transmises correspondent à ma demande.</span>
              </label>
              <div className="text-2xl font-serif" data-testid="ac-total">Total : {PRICE} €</div>
              <button onClick={submit} disabled={!confirmChk || submitting || currentFiles.length === 0} data-testid="ac-submit" className="cs-btn-primary disabled:opacity-50 inline-flex items-center gap-2">
                {submitting ? "Envoi…" : "Confirmer la commande"} <ArrowUpRight size={16} />
              </button>
            </section>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0 || submitting} className="cs-btn-ghost !text-black !border-black disabled:opacity-30" data-testid="ac-prev">← Précédent</button>
          {step < STEPS.length - 1 && <button onClick={() => setStep(s => s + 1)} disabled={!canNext} className="cs-btn-primary disabled:opacity-50" data-testid="ac-next">Suivant →</button>}
        </div>
      </div>
    </div>
  );
}
